<script setup lang="ts">
import {
  computed,
  onMounted,
  PropType,
  shallowRef,
  useId,
  useTemplateRef,
  watch,
} from "vue";
import FeedbackChoice from "@/components/Core/PopupMessages/Feedback/FeedbackChoice.vue";
import FeedbackScale from "@/components/Core/PopupMessages/Feedback/FeedbackScale.vue";
import {
  FEEDBACK_COMMENT_MAX_LENGTH,
  FeedbackAnswer,
  FeedbackQuestion,
} from "@/components/Core/PopupMessages/Feedback/feedbackSurvey";
import { FEEDBACK_COPY } from "@/copy/feedback";

const props = defineProps({
  question: { type: Object as PropType<FeedbackQuestion>, required: true },
  answer: {
    type: [Number, String] as PropType<FeedbackAnswer>,
    default: undefined,
  },
  last: { type: Boolean, default: false },
  followUp: { type: Boolean, default: false },
});
const emit = defineEmits({
  answer: (_value: FeedbackAnswer) => true,
  draft: (_value: string) => true,
  skip: () => true,
  "update:followUp": (_value: boolean) => true,
});

const promptId = useId();
const heading = useTemplateRef("heading");
const textarea = useTemplateRef("textarea");
const scaleAnswer = computed(() =>
  typeof props.answer === "number" ? props.answer : undefined,
);
const choiceAnswer = computed(() =>
  typeof props.answer === "string" ? props.answer : undefined,
);
// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- keyed per question by the parent, so only the initial answer matters
const comment = shallowRef(
  typeof props.answer === "string" ? props.answer : "",
);

const followUpChecked = computed({
  get: () => props.followUp,
  set: (value: boolean) => emit("update:followUp", value),
});
const hasComment = computed(() => comment.value.trim() !== "");
watch(comment, (value) => emit("draft", value));
watch(hasComment, (value) => {
  if (!value && props.followUp) emit("update:followUp", false);
});

onMounted(() =>
  (textarea.value ?? heading.value)?.focus({ preventScroll: true }),
);
</script>

<template>
  <div>
    <h2
      :id="promptId"
      ref="heading"
      tabindex="-1"
      class="text-base leading-snug font-semibold text-gray-900 outline-none"
    >
      {{ question.prompt }}
    </h2>
    <div class="mt-3">
      <FeedbackScale
        v-if="question.type === 'scale'"
        :selected="scaleAnswer"
        :min="question.min"
        :max="question.max"
        :low="question.low"
        :high="question.high"
        :labelledby="promptId"
        @select="emit('answer', $event)"
      />
      <FeedbackChoice
        v-else-if="question.type === 'choice'"
        :options="question.options"
        :inline="question.inline"
        :selected="choiceAnswer"
        :labelledby="promptId"
        @select="emit('answer', $event)"
      />
      <template v-else>
        <textarea
          ref="textarea"
          v-model="comment"
          rows="3"
          :maxlength="FEEDBACK_COMMENT_MAX_LENGTH"
          :aria-labelledby="promptId"
          :placeholder="FEEDBACK_COPY.commentPlaceholder"
          class="block w-full resize-none rounded-md border-gray-300 text-sm shadow-sm focus:border-purple-500 focus:ring-purple-500"
        />
        <label
          v-if="question.followUp"
          class="mt-2 flex items-center gap-2 text-sm text-gray-700"
          :class="{ 'opacity-50': !hasComment }"
        >
          <input
            v-model="followUpChecked"
            type="checkbox"
            :disabled="!hasComment"
            class="size-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          {{ FEEDBACK_COPY.followUp }}
        </label>
        <div class="mt-2 flex justify-end gap-2">
          <button type="button" class="btn-basic btn-sm" @click="emit('skip')">
            {{ FEEDBACK_COPY.skip }}
          </button>
          <button
            type="button"
            class="btn-primary btn-sm"
            :disabled="!hasComment"
            @click="emit('answer', comment.trim())"
          >
            {{ last ? FEEDBACK_COPY.send : FEEDBACK_COPY.next }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
